import d3 from "d3";
import R from "ramda";
import $ from "jquery";
import * as core from "../data/core.js";
import {moveToFront} from "./d3Extensions.js";
import createPopup from "../page/createPopup.js";

export default function createSesScatter(initialState = {}) {

    // defaults
    let state = {
        selector: "",
        width: 0,
        indicators: {},
        locations: {},
        cleanData: {},
        activeIndicator: null,
        onActiveIndicatorChange: () => {}
    };

    let prevState = {};

    setState(initialState);

    function setState(newState) {

        // copy the old state into prevState
        prevState = Object.assign({}, state);

        Object.assign(state, newState);

        // when the active indicator changes...
        if (prevState.activeIndicator !== state.activeIndicator){
            const indicator = state.indicators[state.activeIndicator];
            state.onActiveIndicatorChange(indicator);
        }

        update();
    }


    // constant height for now
    const margin = { top: 20, right: 20, bottom: 25, left: 25 };

    var vizWidth, vizHeight;

    const popup = createPopup({
        container: state.selector,
        offset: -6,
        triangle: "right",
        isHidden: true,
        closeBtn: false
    });

    const svg = d3.select(state.selector).append("svg")
        .style({
            "overflow": "visible" // so "baseline" and "current" don't get cut off
        });

    const g = svg.append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

    // Arrow heads
    const defs = svg.append("defs");

    var dataFormatter,
        getDataPoint,
        getChange,
        getChangeStatus;

    defs
        .append("marker")
        .attr("id", "arrow")
        .attr("markerWidth", 10)
        .attr("markerHeight", 10)
        .attr("refX", 0)
        .attr("refY", 3)
        .attr("orient", "auto-start-reverse")
        .attr("markerUnits", "strokeWidth")
        .append("path")
        .attr("d", "M0,0 L0,6 L5,3 z");

    defs
        .append("pattern")
        .attr("id", "scatter-diagonal")
        .attr("patternUnits", "userSpaceOnUse")
        .attr("width", 12)
        .attr("height", 12)
        .append("path")
        .attr("d", "M-1,1 l 2,-2 M 0,12 l 12,-12 M 11,13 l 10,-10");

    var xScale = d3.scale.linear(),
      yScale = d3.scale.linear();

    function xTickFormat(d, i){
        if(d === xScale.ticks()[0]){
            return "Higher Income";
        }
        else if(d === R.last(xScale.ticks())){
            return "Lower Income";
        }
        return "";
    }

    function yTickFormat(d, i){
        var tickDirection = core.lowerIsBetter(state.indicators[state.activeIndicator])
            ? ["Higher", "Lower"] : ["Lower", "Higher"];

        if(d === yScale.ticks()[0]){
            return tickDirection[0] + " Performing";
        }
        else if(d === R.last(yScale.ticks())){
            return tickDirection[1] + " Performing";
        }
        return "";
    }

    const xAxis = d3.svg.axis()
      .scale(xScale)
      .tickPadding(10)
      .tickFormat(xTickFormat);

    const yAxis = d3.svg.axis()
      .scale(yScale)
      .tickPadding(10)
      .orient("left")
      .tickFormat(yTickFormat);

    function axisArrowsPath(){
        return `M 0 0 L 0 ${vizHeight} L ${vizWidth} ${vizHeight}`;
    }

    function pointDistance(point1, point2){
        return Math.sqrt(Math.pow(point2[0] - point1[0], 2) + Math.pow(point2[1] - point1[1], 2));
    }

    const povertyPercent = R.prop("fplLt200");

    const dotX = R.curry((margin, d) => xScale(povertyPercent(state.locations[d.locId])) + margin),
        dotY = R.curry((margin, d) => yScale(getDataPoint(1, d)) + margin);

    var treeCtr = d3.geom.quadtree()
            .x(dotX(margin.left))
            .y(dotY(margin.top)),
        dotTree;

    var dots,
        activeLocationId;

    var historicDot = g.append("circle").attr("class", "dot old").attr("r", 5),
        historicLabel = g.append("text").attr("class", "historic-label"),
        activeLabel = g.append("text").attr("class", "active-label");

    svg.on("mousemove", function(){

        if(!dots) return;

        const m = d3.mouse(this);

        const point = dotTree.find(m),
            distance = pointDistance(m, [dotX(margin.left, point), dotY(margin.top, point)]);

        if(activeLocationId === point.locId && distance <= 50) return;

        const oldActiveDot = dots.filter(d => d.locId === activeLocationId);
        oldActiveDot.classed("active", false);

        // Only activate if we're close
        if(distance > 50){
            activeLocationId = null;
            hideHoverStuff();
            return;
        }

        svg.classed("dot-hovered", true);

        activeLocationId = point.locId;

        const activeDot = dots.filter(d => d.locId === point.locId);
        activeDot.classed("active", true);
        moveToFront(activeDot);

        const xPosition = dotX(margin.left, activeDot.datum());
        const yPosition = dotY(margin.top, activeDot.datum());
        const location = state.locations[activeLocationId];
        const data = activeDot.datum();
        const showLeft = dotX(0, activeDot.datum()) < vizWidth/2;

        const historicDotValue = getDataPoint(0, activeDot.datum()),
            hasHistoricValue = !R.isNil(historicDotValue);
        const activeDotY = dotY(0, activeDot.datum());

        activeLabel
            .attr("dy", ".6ex")
            .attr("x", xScale(povertyPercent(state.locations[activeLocationId])) +
                (showLeft ? -10 : 10))
            .attr("y", activeDotY)
            .style("text-anchor", showLeft ? "end" : "start")
            // don't show if it's too close to the historic dot
            .classed("show", hasHistoricValue ? Math.abs(yScale(historicDotValue) - activeDotY) > 11 : true)
            .text("Current");
        moveToFront(activeLabel);

        const indicator = state.indicators[state.activeIndicator];

        /** popup variables */
        const title = `<div class="location-label">${location.name}</div>
                <div class="low-income-residents">
                    <span class="number">${Math.round(povertyPercent(location))}<sup>%</sup></span> <span>low-income residents<span>
                </div>
        `;

        const changeHtml = !R.isNil(getDataPoint(0, data))
            ? `<span class="change ${getChangeStatus(data)}">${getChange(data)}</span>`
            : "";

        // only show the baseline if we have data
        const baselineYearValue = getDataPoint(0, data);

        const baselineTitleHtml = (baselineYearValue) ? (
                `<th><div class="year-label">${indicator.years[0]} ${(core.isOverallIndicator(indicator) ? "" : "(Baseline)")}</div></th>`
            ) : "";

        const baselineValueHtml = (baselineYearValue) ? (
            `<td>
                <div className="data-point">
                    <span class="number">${dataFormatter(baselineYearValue, true)}</span>
                </div>
            </td>`
        ) : "";

        const html = $(`
            <div class="ses-scatter__popup">

                <table>
                    <tbody>
                        <tr>
                            ${baselineTitleHtml}
                            <th><div class="year-label">${indicator.years[1]} (Current)</div></th>
                        </tr>
                        <tr>
                            ${baselineValueHtml}
                            <td>
                                <div className="data-point">
                                    <span class="number">${dataFormatter(getDataPoint(1, data), true)}</span>
                                    ${changeHtml}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="indicator-label">${indicator.label}</div>
            </div>
        `);

        // show popup
        popup.setState({
            title: title,
            html: html,
            top:  yPosition,
            left: xPosition,
            offset: -8,
            isHidden: false,
            width: vizWidth/2 - 10, // - 10 for the triangle
            triangle: showLeft ? "left" : "right"
        });

        window.addEventListener("touchstart", handleTouchAwayPopup, true);

        if(hasHistoricValue){

            historicDot
                .attr("cx", xScale(povertyPercent(state.locations[activeLocationId])))
                .attr("cy", yScale(historicDotValue))
                .classed("show", true);

            historicLabel
                .attr("dy", ".6ex")
                .attr("x", xScale(povertyPercent(state.locations[activeLocationId])) +
                    (showLeft ? -10 : 10))
                .attr("y", yScale(historicDotValue))
                .style("text-anchor", showLeft ? "end" : "start")
                .classed("show", true)
                .text("Baseline");

            moveToFront(historicDot);
            moveToFront(historicLabel);
        }
        else{
            historicDot.classed("show", false);
            historicLabel.classed("show", false);
        }

    })
    .on("mouseleave", hideHoverStuff);


    function hideHoverStuff(){
        activeLocationId = null;
        dots.classed("active", false);
        historicDot.classed("show", false);
        historicLabel.classed("show", false);
        activeLabel.classed("show", false);
        svg.classed("dot-hovered", false);
        popup.hidePopup();
        window.removeEventListener("touchstart", handleTouchAwayPopup, true);
    }

    function handleTouchAwayPopup(e){
        if (!$(e.target).closest(state.selector).length){
            hideHoverStuff();
        }
    }

    function update() {

        // don't render if we don't have a width yet
        if (!state.width) { return; }

        const indicator = state.indicators[state.activeIndicator];

        getDataPoint = core.getData(indicator);
        getChange = (data) => {
            const value = core.getChange(indicator, data);
            return (value >= 0) ? `+${value}` : value;
        };
        dataFormatter = core.formatValue(indicator),
        getChangeStatus = core.getChangeStatus(indicator);


        // Keep a standard aspect ratio
        var height = state.width;

        vizWidth = state.width - margin.left - margin.right,
        vizHeight = height - margin.top - margin.bottom;

        svg.attr("width", vizWidth + margin.left + margin.right)
            .attr("height", vizHeight + margin.top + margin.bottom);

        xScale.range([0, vizWidth]);
        yScale.range([vizHeight, 0]);

        xAxis.tickSize(-vizHeight);
        yAxis.tickSize(-vizWidth);

        const indicatorData = R.values(state.cleanData[state.activeIndicator]).filter(d => d.locId !== 0 && !R.isNil(getDataPoint(1, d)));

        const maxYVal = d3.max(indicatorData, (d) => Math.max(getDataPoint(0, d), getDataPoint(1, d))),
            maxXVal = d3.max(R.values(state.locations), povertyPercent),
            povertyThreshold = 40,
            performanceThreshold = d3.median(indicatorData, getDataPoint(1));


        // add padding to the right side so there is room for the hover label
        xScale.domain([0, maxXVal * 1.005]).nice();
        yScale.domain([0, maxYVal]).nice();

        dotTree = treeCtr(indicatorData);

        dots = g.selectAll("circle.dot.new").data(indicatorData, x => x.locId);

        dots.enter().append("circle").attr("class", "dot new");

        dots.exit().remove();

        dots
            .transition()
            .attr("cx", dotX(0))
            .attr("cy", dotY(0))
            .attr("r", 5);

        var xAxisElement = g.select("g.axis.x");

        if (xAxisElement.empty()) {
            xAxisElement = g.append("g").attr("class", "x axis");
        }

        xAxisElement
            .attr("transform", "translate(0," + vizHeight + ")")
            .call(xAxis)
            .selectAll("text")
            .style("text-anchor", (d, i) => i === 0? "start": "end");

        var yAxisElement = g.select("g.axis.y");

        if (yAxisElement.empty()) {
            yAxisElement = g.append("g").attr("class", "y axis");

            g.append("text")
                .attr("class", "median")
                .text("Median");

            g.append("text")
                .attr("class", "pov")
                .text("40% residents below 200% FPL");
        }

        yAxisElement
            .call(yAxis)
            .selectAll("text")
            .attr("transform", "rotate(-90)translate(10,-13)")
            .style("text-anchor", (d, i) => i === 0? "start": "end");

        g.select("text.median")
            // .attr("transform", `rotate(-90,-8,${yScale(performanceThreshold)})`)
            .attr("x", 4)
            .attr("y", yScale(performanceThreshold))
            .attr("dy", -4)
            .style("text-anchor", "start");

        g.select("text.pov")
            .attr("x", xScale(povertyThreshold) + 60)
            .attr("y", vizHeight + 19)
            .style("text-anchor", "end");

        var axisArrows = g.select("g.axis.arrows");

        if(axisArrows.empty()){
            axisArrows = g.append("g")
                .attr("class", "axis arrows");

            axisArrows
                .append("path")
                .attr("marker-end", "url(#arrow)")
                .attr("marker-start", "url(#arrow)");
        }

        axisArrows.select("path")
            .attr("d", axisArrowsPath);

        var povertyThresholdLine = g.select("line.threshold.poverty");

        if(povertyThresholdLine.empty()){
            povertyThresholdLine = g.append("line").attr("class", "threshold poverty");
        }

        povertyThresholdLine
            .attr("x1", xScale(povertyThreshold))
            .attr("x2", xScale(povertyThreshold))
            .attr("y1", 0)
            .attr("y2", vizHeight);

        var performanceThresholdLine = g.select("line.threshold.performance");

        if(performanceThresholdLine.empty()){
            performanceThresholdLine = g.append("line").attr("class", "threshold performance");
        }

        performanceThresholdLine
            .attr("x1", 0)
            .attr("x2", vizWidth)
            .attr("y1", yScale(performanceThreshold))
            .attr("y2", yScale(performanceThreshold));

        var highlightRectangle = g.select("rect.highlight");

        if(highlightRectangle.empty()){
            highlightRectangle = g.append("rect").attr("class", "highlight");
        }

        highlightRectangle
            .attr("x", xScale(povertyThreshold))
            .attr("y", core.lowerIsBetter(state.indicators[state.activeIndicator])
                ? yScale(performanceThreshold)
                : 0)
            .attr("width", vizWidth - xScale(povertyThreshold))
            .attr("height", core.lowerIsBetter(state.indicators[state.activeIndicator])
                ? vizHeight - yScale(performanceThreshold)
                : yScale(performanceThreshold))
            .attr("fill", "url(#scatter-diagonal)");

        var highPovertyHighPerfLabel = g.select("text.pov-perf-label");

        if(highPovertyHighPerfLabel.empty()){
            highPovertyHighPerfLabel = g.append("text").attr("class", "pov-perf-label");
            highPovertyHighPerfLabel.append("tspan")
                .attr("dy", "-1em")
                .text("Lower Income /");
            highPovertyHighPerfLabel.append("tspan")
                .attr("dy", "1em")
                .text("higher performing");
        }

        highPovertyHighPerfLabel
            .attr("y", core.lowerIsBetter(state.indicators[state.activeIndicator])
                ? vizHeight - 7
                : yScale(performanceThreshold) - 7
            );

        highPovertyHighPerfLabel.selectAll("tspan")
            .attr("x", (vizWidth - xScale(povertyThreshold))/2 + xScale(povertyThreshold));

        moveToFront(dots);
    }


    update();


    // external api
    return {
        // height and width are the same
        setSize: (size) => setState({ width: size }),
        setActiveIndicator: (ind) => setState({activeIndicator: ind})
    };
}
